import React, { useState, useEffect, useMemo, Fragment } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { imgUrl } from "src/actions/types";
import { formatCurrency } from "../utils/currencyHelper";
// --- Import necessary Redux actions ---
import {
  getAllAppointments,
  updateAppointment,
} from "../../actions/appointmentActions";
import { getAllPatientProfiles } from "../../actions/patientProfileActions";
import { getAllDoctors } from "../../actions/doctorActions";
import { listAllInvoices } from "../../actions/invoiceActions";

// --- Date Helper Functions ---
const formatTableDate = (dateString, timeString) => {
  if (!dateString || !timeString) return "";
  const date = new Date(dateString);
  return (
    <>
      {date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })}
      <span className="text-primary d-block">{timeString}</span>
    </>
  );
};
// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//     maximumFractionDigits: 0,
//   }).format(amount || 0);
// };

// --- Helper for Transaction Status Badge ---
const getBadgeClassForTransaction = (status) => {
  switch (status) {
    case "Paid":
      return "bg-success-light";
    case "Pending":
      return "bg-warning-light";
    case "Cancelled":
      return "bg-danger-light";
    case "Failed":
      return "bg-danger-light";
    default:
      return "bg-light text-dark";
  }
};

// --- Main Admin Appointments Component ---
const AppointmentList = ({
  appointments,
  invoices,
  patients,
  doctors,
  isLoading,
  selectedCurrency, 
  getAllAppointments,
  getAllPatientProfiles,
  getAllDoctors,
  listAllInvoices,
  updateAppointment,
}) => {
  // --- State for Filters ---
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // State for status updates
  const [updatingId, setUpdatingId] = useState(null);

  // Fetch all necessary data on component mount
  useEffect(() => {
    getAllAppointments();
    getAllPatientProfiles();
    getAllDoctors();
    listAllInvoices();
  }, [
    getAllAppointments,
    getAllPatientProfiles,
    getAllDoctors,
    listAllInvoices,
  ]);

  // --- (MODIFIED) Memoized hook with corrected data type matching ---
  const enrichedAppointments = useMemo(() => {
    if (
      appointments.length === 0 ||
      patients.length === 0 ||
      doctors.length === 0
    ) {
      return [];
    }
    const patientMap = new Map(patients.map((p) => [p.PatientID, p]));
    const doctorMap = new Map(doctors.map((d) => [d.hospitalID, d]));

    // 1. Create the invoice map using STRING keys to prevent type mismatch
    const invoiceMap = new Map(
      invoices.map((inv) => [String(inv.appointmentId), inv])
    );

    return appointments.map((apt) => {
      // 2. Look up the appointment's ID as a STRING
      const matchingInvoice = invoiceMap.get(String(apt.appointmentId));

      return {
        ...apt,
        patient: patientMap.get(apt.PatientID) || {},
        doctor: doctorMap.get(apt.hospitalID) || {},
        transactionStatus: matchingInvoice?.Status || "N/A",
      };
    });
  }, [appointments, invoices, patients, doctors]);

  // Memoized hook to apply filters
  const displayedAppointments = useMemo(() => {
    let filtered = enrichedAppointments;

    if (statusFilter !== "All") {
      filtered = filtered.filter((apt) => apt.status === statusFilter);
    }
    if (dateFilter) {
      filtered = filtered.filter((apt) => apt.appointmentDate === dateFilter);
    }
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (apt) =>
          `${apt.doctor.firstName} ${apt.doctor.lastName}`
            .toLowerCase()
            .includes(lowerCaseSearch) ||
          `${apt.patient.firstName} ${apt.patient.lastName}`
            .toLowerCase()
            .includes(lowerCaseSearch)
      );
    }

    return filtered.sort(
      (a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate)
    );
  }, [enrichedAppointments, statusFilter, dateFilter, searchTerm]);
 const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayedAppointments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayedAppointments.length / itemsPerPage);

  // Reset to page 1 if filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, itemsPerPage]);
  // Handler for changing the APPOINTMENT status (Confirmed/Canceled)
  const handleStatusUpdate = async (appointment, newStatus) => {
    setUpdatingId(appointment.appointmentId);
    try {
      await updateAppointment({
        appointmentId: appointment.appointmentId,
        status: newStatus,
      });
      await getAllAppointments();
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setUpdatingId(null);
    }
  };
const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const renderItemsPerPageSelector = () => (
    <div className="dataTables_length">
      <label>Items: 
        <select className="form-control form-control-sm ml-2" value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} style={{ width: '70px', display: 'inline-block' }}>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </label>
    </div>
  );

 const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; 
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className="pagination-controls-wrapper">
            <div className="dataTables_info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, displayedAppointments.length)} of {displayedAppointments.length} entries
            </div>
            <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                </li>
                
                {startPage > 1 && (
                    <>
                        <li className="page-item"><button className="page-link" onClick={() => handlePageChange(1)}>1</button></li>
                        {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                    </>
                )}

                {pageNumbers.map(number => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(number)}>{number}</button>
                    </li>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        <li className="page-item"><button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button></li>
                    </>
                )}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                </li>
            </ul>
        </div>
    );
};
  return (
    <Fragment>
      <style>{`
    .pagination-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        margin-top: 20px;
        padding: 15px 0;
        border-top: 1px solid #dee2e6;
    }
    
    .items-per-page-wrapper {
        flex: 0 0 auto;
    }
    
    .pagination-controls-wrapper {
        flex: 1 1 auto;
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }
    
    .dataTables_length label {
        display: inline-flex;
        align-items: center;
        font-weight: normal;
        margin-bottom: 0;
    }
    
    .dataTables_length select {
        margin-left: 10px;
        width: 70px;
        border-radius: 4px;
        border: 1px solid #ddd;
        padding: 5px;
    }
    
    .pagination {
        margin: 0;
        padding: 0;
    }
    
    .pagination .page-item.active .page-link {
        background-color: #0084d9;
        border-color: #0084d9;
        color: white;
    }
    
    .pagination .page-link {
        color: #0084d9;
        padding: 0.5rem 0.75rem;
        border-radius: 4px;
        margin: 0 2px;
    }
    
    .dataTables_info {
        font-size: 0.9rem;
        color: #6c757d;
        margin-right: 20px;
    }

    @media (max-width: 768px) {
        .pagination-wrapper {
            flex-direction: column;
            align-items: center;
        }
        .pagination-controls-wrapper {
            flex-direction: column;
            margin-top: 15px;
        }
        .dataTables_info {
            margin-right: 0;
            margin-bottom: 10px;
        }
    }
`}</style>
      <Helmet>
        <title>Appointments | Admin Dashboard</title>
      </Helmet>

      <div className="page-wrapper ml-0">
        <div className="content-container">
          <div className="page-header">
            <div className="">
              {/* <div className="col-sm-12">
                <h3 className="page-title">Appointments</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Appointments</li>
                </ul>
              </div> */}
              <div className="page-header filter-bar rounded">
                <div className="row align-items-center">
                  <div className="col">
                    <h4 className="page-title"> Appointments </h4>
                    <ul className="breadcrumb">
                      <li className="breadcrumb-item">
                        <Link to="/doctor/dashboard">Admin Dashboard</Link>
                      </li>
                      <li className="breadcrumb-item active">Appointments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- Filter Card --- */}
          <div className="card">
            <div className="card-body">
              <form className="row form-row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Search Doctor or Patient</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Appointment Status</label>
                    <select
                      className="form-control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All</option>
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Canceled">Canceled</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="datatable table table-hover table-center mb-0">
                      <thead>
                        <tr>
                          <th>APT ID</th>
                          <th>Doctor Name</th>
                          <th>Patient Name</th>
                          <th>Appointment Time</th>
                          <th>Appt.Status</th>
                          <th className="text-center">Payment Status</th>
                          <th className="text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading && displayedAppointments.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center p-5">
                              <div className="spinner-border text-primary"></div>
                            </td>
                          </tr>
                        ) : displayedAppointments.length > 0 ? (
                          currentItems.map((apt) => {
                            const patientImg = apt.patient.uploadPhoto
                              ? `${imgUrl}${apt.patient.uploadPhoto}`
                              : "/assets/img/patients/patient.jpg";
                            const doctorImg = apt.doctor.uploadPhoto
                              ? `${imgUrl}${apt.doctor.uploadPhoto}`
                              : "/assets/img/doctors/doctor-thumb-01.jpg";
                            const isUpdating = updatingId === apt.appointmentId;

                            return (
                              <tr key={apt.appointmentId}>
                                <td>{apt.appointmentId}</td>
                                <td>
                                  <h2 className="table-avatar">
                                    <span className="avatar avatar-sm mr-2">
                                      <img
                                        className="avatar-img rounded-circle"
                                        src={doctorImg}
                                        alt="Doctor"
                                      />
                                    </span>
                                    <strong>
                                      Dr. {apt.doctor.firstName}{" "}
                                      {apt.doctor.lastName}
                                    </strong>
                                  </h2>
                                </td>
                                <td>
                                  <h2 className="table-avatar">
                                    <span className="avatar avatar-sm mr-2">
                                      <img
                                        className="avatar-img rounded-circle"
                                        src={patientImg}
                                        alt="Patient"
                                      />
                                    </span>
                                    <strong>
                                      {apt.patient.firstName}{" "}
                                      {apt.patient.lastName}
                                    </strong>
                                  </h2>
                                </td>
                                <td>
                                  {formatTableDate(
                                    apt.appointmentDate,
                                    apt.appointmentTime
                                  )}
                                </td>
                                {/* <td>
                                                                    {isUpdating ? (
                                                                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                                    ) : (
                                                                        <div className="status-toggle">
                                                                            <input type="checkbox" id={`status_${apt.appointmentId}`} className="check" checked={apt.status === 'Confirmed'} onChange={(e) => handleStatusUpdate(apt, e.target.checked ? 'Confirmed' : 'Canceled')} />
                                                                            <label htmlFor={`status_${apt.appointmentId}`} className="checktoggle">checkbox</label>
                                                                        </div>
                                                                    )}
                                                                </td> */}
                                <td>{apt.status}</td>
                                <td className="text-center">
                                  <span
                                    className={
                                      apt.transactionStatus &&
                                      apt.transactionStatus.toLowerCase() ===
                                        "paid"
                                        ? "paid-label"
                                        : apt.transactionStatus &&
                                          apt.transactionStatus.toLowerCase() ===
                                            "failed"
                                        ? "failed-label"
                                        : apt.transactionStatus &&
                                          apt.transactionStatus.toLowerCase() ===
                                            "unpaid"
                                        ? "unpaid-label"
                                        : apt.transactionStatus &&
                                          apt.transactionStatus.toLowerCase() ===
                                            "pending"
                                        ? "pending-label"
                                        : "unpublish-light-label"
                                    }
                                  >
                                    {apt.transactionStatus}
                                  </span>
                                </td>
                                <td className="text-right">
                                  {formatCurrency(apt.paidAmount || 0, selectedCurrency)}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="7"
                              className="text-center text-muted p-5"
                            >
                              No appointments match the current filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {displayedAppointments.length > 0 && (
    <div className="pagination-wrapper">
        <div className="items-per-page-wrapper">
            {renderItemsPerPageSelector()}
        </div>
        {renderPagination()}
    </div>
)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

// --- Redux Connection ---
const mapStateToProps = (state) => ({
  appointments: state.appointment.all || [],
  invoices: state.invoice.invoices || [],
  patients: state.patientProfile.profiles || [],
  doctors: state.doctorList.doctors || [],
   selectedCurrency: state.currencyReducer.selectedCurrency, 
  isLoading:
    state.appointment.loading ||
    state.patientProfile.loading ||
    state.doctorList.loading ||
    state.invoice.loading,
});

const mapDispatchToProps = {
  getAllAppointments,
  getAllPatientProfiles,
  getAllDoctors,
  listAllInvoices,
  updateAppointment,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppointmentList);
